import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AuditLogData,
  isCourseAuditLog,
  isEventAuditLog,
  isOrderAuditLog,
  isOrderPageAuditLog,
  isPassAuditLog,
  isPaymentLinkAuditLog,
  isUserAuditLog,
  isVoucherAuditLog,
  PASS_ORDER_PAGE_AUDIT_TYPES,
} from '../types';
import { mb } from '../../common/theme/mbTokens';
import { OrderPageData, PassOrderPageData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { useLanguage } from '../../common/GeneralUtils';
import { getOrderPages, getPassOrderPages } from '../../utils/AccountUtils';

type AuditLogEntityLinkProps = {
  log: AuditLogData;
  onOrderClick?: (orderId: string) => void;
};

const LINK_SX: React.CSSProperties = {
  color: mb.green,
  textDecoration: 'underline',
  cursor: 'pointer',
};

export const AuditLogEntityLink: React.FC<AuditLogEntityLinkProps> = ({ log, onOrderClick }) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const language = useLanguage();

  const getEntityText = (): string => {
    if (isOrderAuditLog(log)) {
      return t('auditlog.entity_info.order', { orderNumber: log.order_number });
    }

    if (isEventAuditLog(log)) {
      return t('auditlog.entity_info.event');
    }

    if (isVoucherAuditLog(log)) {
      return t('auditlog.entity_info.voucher', { orderNumber: log.order_number });
    }

    if (isPaymentLinkAuditLog(log)) {
      return t('auditlog.entity_info.payment_link', { paymentLinkNumber: log.payment_link_number });
    }

    if (isCourseAuditLog(log)) {
      return t('auditlog.entity_info.course', { orderNumber: log.order_number });
    }

    if (isPassAuditLog(log)) {
      return t('auditlog.entity_info.pass', { orderNumber: log.order_number });
    }

    if (isUserAuditLog(log)) {
      return t('auditlog.entity_info.user', { email: log.target_user_email });
    }

    if (isOrderPageAuditLog(log)) {
      const pages: Array<OrderPageData | PassOrderPageData> = PASS_ORDER_PAGE_AUDIT_TYPES.has(log.audit_type)
        ? getPassOrderPages(authState?.account, true)
        : getOrderPages(authState?.account, true);
      const label = pages.find((page) => page.id === log.order_page_id)?.label?.[language];
      return label || t('auditlog.entity_info.unknown');
    }

    return t('auditlog.entity_info.unknown');
  };

  const getEntityLink = (): string | null => {
    if (isEventAuditLog(log)) {
      return `/dashboard/calendar?entityid=${log.event_id}`;
    }

    if (isVoucherAuditLog(log)) {
      return `/dashboard/vouchers`;
    }

    if (isPaymentLinkAuditLog(log)) {
      return `/dashboard/payment-links`;
    }

    if (isCourseAuditLog(log)) {
      return `/dashboard/courses`;
    }

    if (isPassAuditLog(log)) {
      return `/dashboard/passes?entityid=${log.pass_id}`;
    }

    if (isOrderPageAuditLog(log)) {
      return PASS_ORDER_PAGE_AUDIT_TYPES.has(log.audit_type)
        ? `/dashboard/pass-settings/${log.order_page_id}`
        : `/dashboard/settings/${log.order_page_id}`;
    }

    return null;
  };

  const entityText = getEntityText();
  const entityLink = getEntityLink();

  if (isOrderAuditLog(log) && onOrderClick) {
    return (
      <span
        role="button"
        tabIndex={0}
        style={LINK_SX}
        onClick={() => onOrderClick(log.order_id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onOrderClick(log.order_id);
          }
        }}>
        {entityText}
      </span>
    );
  }

  if (entityLink) {
    return (
      <Link
        to={entityLink}
        style={LINK_SX}
        onMouseEnter={(e) => {
          e.currentTarget.style.textDecoration = 'underline';
          e.currentTarget.style.color = mb.greenHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.textDecoration = 'underline';
          e.currentTarget.style.color = mb.green;
        }}>
        {entityText}
      </Link>
    );
  }

  return <span>{entityText}</span>;
};
